import {memo, useRef} from 'react'
import {
  isTrackReference,
  useIsMuted,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react'
import {Maximize2, Minimize2} from 'lucide-react'
import {Track} from 'livekit-client'
import {cn} from '@/shared/lib/cn'
import {useIsParticipantSpeaking} from '../../speaking-context'
import {ParticipantTile} from './ParticipantTile'
import {useTileFullscreen} from './useTileFullscreen'

interface VideoTileProps {
  trackRef: TrackReferenceOrPlaceholder
  isFocused?: boolean
  onClick?: () => void
  className?: string
}

export const VideoTile = memo(function VideoTile({
  trackRef,
  isFocused,
  onClick,
  className,
}: VideoTileProps) {
  const isSpeaking =
    useIsParticipantSpeaking(trackRef.participant.identity) &&
    trackRef.source !== Track.Source.ScreenShare

  const isScreenShare = trackRef.source === Track.Source.ScreenShare
  const hasVideoPublication =
    isTrackReference(trackRef) &&
    (trackRef.publication?.kind === 'video' ||
      trackRef.source === Track.Source.Camera ||
      isScreenShare)
  const isCameraMuted = useIsMuted(trackRef)
  const showVideo = hasVideoPublication && !isCameraMuted

  const tileRef = useRef<HTMLDivElement>(null)
  const {isFullscreen, canFullscreen, toggleFullscreen, exitFullscreen} = useTileFullscreen(tileRef)

  return (
    <div
      ref={tileRef}
      className={cn(
        'group relative flex cursor-pointer overflow-hidden transition-shadow',
        isFullscreen ? 'rounded-none' : 'rounded-lg',
        !isSpeaking && 'hover:ring-2 hover:ring-border',
        isSpeaking && 'ring-2 ring-signal-success/80',
        className,
      )}
      onClick={e => {
        if (isFullscreen) {
          e.stopPropagation()
          exitFullscreen()
          return
        }
        onClick?.()
      }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (isFullscreen) {
            exitFullscreen()
            return
          }
          onClick?.()
        }
      }}
      aria-label={isFocused ? 'Открепить участника' : 'Закрепить участника'}
    >
      <ParticipantTile
        trackRef={trackRef}
        className="h-full w-full"
        isFocused={isFocused || isFullscreen}
        isFullscreen={isFullscreen}
      />

      {showVideo && canFullscreen && (
        <button
          className={cn(
            'absolute top-2 right-2 z-10 flex items-center justify-center',
            'size-7 rounded-md',
            'bg-foreground/40 text-foreground/70 backdrop-blur-sm',
            'opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100',
            'hover:bg-foreground/60 hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'cursor-pointer',
          )}
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Выйти из полноэкранного режима' : 'Полноэкранный режим'}
          tabIndex={0}
        >
          {isFullscreen ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
        </button>
      )}
    </div>
  )
})
